import { collection, addDoc, deleteDoc, doc, getDocs, query, where, onSnapshot, updateDoc, getDoc } from 'firebase/firestore';
import { db } from './config';
import { getUserProfileByUid } from './firestore';

// Friend Types
export interface Friend {
  id: string;
  userId: string;
  friendId: string;
  friendName: string;
  friendHandle: string;
  friendImageUrl?: string;
  friendRole?: 'user' | 'admin' | 'moderator';
  status: 'pending' | 'accepted' | 'blocked';
  createdAt: Date;
}

export interface FriendRequest {
  id: string;
  fromUserId: string;
  toUserId: string;
  fromUserName: string;
  fromUserHandle: string;
  fromUserImageUrl?: string;
  status: 'pending' | 'accepted' | 'declined';
  createdAt: Date;
}

// Friend Functions
export const sendFriendRequest = async (
  fromUserId: string,
  toUserId: string,
  fromUserName: string,
  fromUserHandle: string,
  fromUserImageUrl?: string
): Promise<string> => {
  const friendRequest: Omit<FriendRequest, 'id' | 'createdAt'> = {
    fromUserId,
    toUserId,
    fromUserName,
    fromUserHandle,
    fromUserImageUrl,
    status: 'pending'
  };

  const docRef = await addDoc(collection(db, 'friendRequests'), {
    ...friendRequest,
    createdAt: new Date()
  });
  
  return docRef.id;
};

export const acceptFriendRequest = async (requestId: string): Promise<void> => {
  // Update the friend request status
  await updateDoc(doc(db, 'friendRequests', requestId), {
    status: 'accepted'
  });

  // Get the request details
  const requestDoc = await getDoc(doc(db, 'friendRequests', requestId));
  if (requestDoc.exists()) {
    const requestData = requestDoc.data() as FriendRequest;
    
    // Get user profiles to include role information
    const [fromUserProfile, toUserProfile] = await Promise.all([
      getUserProfileByUid(requestData.fromUserId),
      getUserProfileByUid(requestData.toUserId)
    ]);
    
    // Create friendship records for both users
    await addDoc(collection(db, 'friends'), {
      userId: requestData.fromUserId,
      friendId: requestData.toUserId,
      friendName: toUserProfile?.displayName || requestData.fromUserName,
      friendHandle: toUserProfile?.handle || requestData.fromUserHandle,
      friendImageUrl: toUserProfile?.profileImageUrl || requestData.fromUserImageUrl,
      friendRole: toUserProfile?.role || 'user',
      status: 'accepted',
      createdAt: new Date()
    });

    await addDoc(collection(db, 'friends'), {
      userId: requestData.toUserId,
      friendId: requestData.fromUserId,
      friendName: fromUserProfile?.displayName || requestData.fromUserName,
      friendHandle: fromUserProfile?.handle || requestData.fromUserHandle,
      friendImageUrl: fromUserProfile?.profileImageUrl || requestData.fromUserImageUrl,
      friendRole: fromUserProfile?.role || 'user',
      status: 'accepted',
      createdAt: new Date()
    });
  }
};

export const declineFriendRequest = async (requestId: string): Promise<void> => {
  await updateDoc(doc(db, 'friendRequests', requestId), {
    status: 'declined'
  });
};

export const getFriendRequests = async (userId: string): Promise<FriendRequest[]> => {
  const q = query(
    collection(db, 'friendRequests'),
    where('toUserId', '==', userId),
    where('status', '==', 'pending')
  );
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FriendRequest));
};

export const getFriends = async (userId: string): Promise<Friend[]> => {
  const q = query(
    collection(db, 'friends'),
    where('userId', '==', userId),
    where('status', '==', 'accepted')
  );
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Friend));
};

export const removeFriend = async (friendshipId: string): Promise<void> => {
  await deleteDoc(doc(db, 'friends', friendshipId));
};

// Check friend status between two users
export const getFriendStatus = async (currentUserId: string, targetUserId: string): Promise<'friends' | 'pending_sent' | 'pending_received' | 'not_friends'> => {
  // Check if they are already friends
  const friendsQuery = query(
    collection(db, 'friends'),
    where('userId', '==', currentUserId),
    where('friendId', '==', targetUserId),
    where('status', '==', 'accepted')
  );
  const friendsSnapshot = await getDocs(friendsQuery);
  
  if (!friendsSnapshot.empty) {
    return 'friends';
  }

  // Check for pending friend requests
  const sentRequestQuery = query(
    collection(db, 'friendRequests'),
    where('fromUserId', '==', currentUserId),
    where('toUserId', '==', targetUserId),
    where('status', '==', 'pending')
  );
  const sentRequestSnapshot = await getDocs(sentRequestQuery);
  
  if (!sentRequestSnapshot.empty) {
    return 'pending_sent';
  }

  const receivedRequestQuery = query(
    collection(db, 'friendRequests'),
    where('fromUserId', '==', targetUserId),
    where('toUserId', '==', currentUserId),
    where('status', '==', 'pending')
  );
  const receivedRequestSnapshot = await getDocs(receivedRequestQuery);
  
  if (!receivedRequestSnapshot.empty) {
    return 'pending_received';
  }

  return 'not_friends';
};

// Unfriend a user (remove both friendship records)
export const unfriendUser = async (currentUserId: string, targetUserId: string): Promise<void> => {
  // Find and delete both friendship records
  const friendship1Query = query(
    collection(db, 'friends'),
    where('userId', '==', currentUserId),
    where('friendId', '==', targetUserId)
  );
  const friendship1Snapshot = await getDocs(friendship1Query);
  
  const friendship2Query = query(
    collection(db, 'friends'),
    where('userId', '==', targetUserId),
    where('friendId', '==', currentUserId)
  );
  const friendship2Snapshot = await getDocs(friendship2Query);

  // Delete both friendship records
  const deletePromises: Promise<void>[] = [];
  
  friendship1Snapshot.docs.forEach(doc => {
    deletePromises.push(deleteDoc(doc.ref));
  });
  
  friendship2Snapshot.docs.forEach(doc => {
    deletePromises.push(deleteDoc(doc.ref));
  });

  await Promise.all(deletePromises);
};

// Cancel a pending friend request
export const cancelFriendRequest = async (currentUserId: string, targetUserId: string): Promise<void> => {
  const requestQuery = query(
    collection(db, 'friendRequests'),
    where('fromUserId', '==', currentUserId),
    where('toUserId', '==', targetUserId),
    where('status', '==', 'pending')
  );
  
  const requestSnapshot = await getDocs(requestQuery);
  
  const deletePromises = requestSnapshot.docs.map(doc => deleteDoc(doc.ref));
  await Promise.all(deletePromises);
};

// Real-time friend requests listener
export const subscribeToFriendRequests = (
  userId: string,
  callback: (requests: FriendRequest[]) => void
) => {
  const q = query(
    collection(db, 'friendRequests'),
    where('toUserId', '==', userId),
    where('status', '==', 'pending')
  );

  return onSnapshot(q, (snapshot) => {
    const requests = snapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    } as FriendRequest));
    callback(requests);
  });
};

// Real-time friends listener
export const subscribeToFriends = (
  userId: string,
  callback: (friends: Friend[]) => void
) => {
  const q = query(
    collection(db, 'friends'),
    where('userId', '==', userId),
    where('status', '==', 'accepted')
  );

  return onSnapshot(q, (snapshot) => {
    const friends = snapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    } as Friend));
    callback(friends);
  });
};
